import { useCampaignStore } from '@/stores/campaignStore';

/** The selected campaign's IANA timezone (undefined → callers fall back to browser-local). */
export function campaignTimeZoneId(): string | undefined {
  return useCampaignStore.getState().selectedCampaign?.timeZoneId;
}

/**
 * Conversions between an `<input type="datetime-local">` value (zone-less wall-clock
 * "yyyy-MM-ddTHH:mm") and a UTC ISO instant, interpreted in the CAMPAIGN timezone — never the
 * browser's (root CLAUDE.md "Campaign Timezone"). Falls back to browser-local when no zone is given.
 */

function wallClockPartsInZone(instant: Date, timeZoneId: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZoneId,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute') };
}

/** Campaign wall-clock "yyyy-MM-ddTHH:mm" → UTC ISO. */
export function campaignLocalInputToUtcIso(local: string, timeZoneId?: string): string {
  if (!timeZoneId) return new Date(local).toISOString();
  const [date, time] = local.split('T');
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const asIfUtc = Date.UTC(y, mo - 1, d, h, mi);
  // The zone's offset at (approximately) that instant: how far its wall clock sits from UTC.
  const seen = wallClockPartsInZone(new Date(asIfUtc), timeZoneId);
  const offset = Date.UTC(seen.y, seen.mo - 1, seen.d, seen.h, seen.mi) - asIfUtc;
  return new Date(asIfUtc - offset).toISOString();
}

/** UTC ISO → campaign wall-clock "yyyy-MM-ddTHH:mm" (for pre-filling a datetime-local input). */
export function utcIsoToCampaignLocalInput(iso: string, timeZoneId?: string): string {
  const instant = new Date(iso);
  if (!timeZoneId) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`
      + `T${pad(instant.getHours())}:${pad(instant.getMinutes())}`;
  }
  const p = wallClockPartsInZone(instant, timeZoneId);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}
